/**
 * `PageProjection` — the SOLE overlay-projection seam (ScaleContext).
 *
 * The bug-2/3/4 render class (docable-editor-overlay-render-260910) shared one
 * root: overlay geometry was derived imperatively, per re-layout event, from a
 * bare Y-flip + uniform scale that assumed the IR bbox frame equalled the
 * pdf.js-viewport frame. That assumption breaks on any page with a non-zero
 * CropBox origin or a `/Rotate` — the IR bboxes are MediaBox-origin, unrotated
 * PDF user space, while the rendered canvas is CropBox-clipped and `/Rotate`-
 * applied (design `docs/design/editor-overlay-scalecontext-260910.md` §1).
 *
 * This module adopts the pdf.js `viewport.transform` as the SINGLE frame
 * authority (design Option A / Ph1b G-1): the same `[a,b,c,d,e,f]` affine that
 * pdf.js computes to rasterize the page — with CropBox offset, `/Rotate`,
 * Y-flip and render-scale ALL baked in — is carried on the page descriptor as a
 * plain value and applied here with pure matrix math. There is exactly ONE
 * definition of the frame; the overlay layer can never disagree with the
 * raster (A.19). No crop/rotation branching lives in this module — the matrix
 * carries all of it (Ph1b G-3).
 *
 * Pure: no DOM, no pdf.js import at projection time. jsdom/node-testable.
 *
 * @module editor/page-projection
 */
/** Apply the 6-tuple affine to a user-space point → output-space point. */
function applyAffine(m, x, y) {
    return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}
/**
 * Project a PDF-user-space bbox `[x, y, w, h]` (origin bottom-left, +Y up) to a
 * canvas CSS-pixel rect `{x, y, w, h}` (origin top-left, +Y down).
 *
 * The four corners are mapped through `userSpaceToDevice`, then the axis-
 * aligned bounding rect of the mapped corners is taken (correct for every
 * `/Rotate ∈ {0,90,180,270}` — a rotated rect stays axis-aligned) and scaled by
 * `cssPxPerDevicePx`. Degenerate `w=0` / `h=0` inputs (an MCID centroid point)
 * project to a zero-size rect at the mapped point — the min/normalize never
 * chokes on them.
 */
export function projectBbox(bbox, proj) {
    const [bx, by, bw, bh] = bbox;
    const m = proj.userSpaceToDevice;
    const corners = [
        applyAffine(m, bx, by),
        applyAffine(m, bx + bw, by),
        applyAffine(m, bx, by + bh),
        applyAffine(m, bx + bw, by + bh),
    ];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [cx, cy] of corners) {
        if (cx < minX)
            minX = cx;
        if (cx > maxX)
            maxX = cx;
        if (cy < minY)
            minY = cy;
        if (cy > maxY)
            maxY = cy;
    }
    const k = proj.cssPxPerDevicePx;
    return {
        x: minX * k,
        y: minY * k,
        w: (maxX - minX) * k,
        h: (maxY - minY) * k,
    };
}
/**
 * Inverse of {@link projectBbox}: un-project a canvas CSS-pixel rect
 * `{x, y, w, h}` (origin top-left) back to a PDF-user-space bbox `[x, y, w, h]`
 * (origin bottom-left). Used by draw-to-create so a box drawn on a rotated or
 * cropped page saves to the correct user-space coordinates.
 *
 * Applies the analytic inverse of `userSpaceToDevice` to the rect corners (after
 * undoing `cssPxPerDevicePx`), then takes the axis-aligned bounding rect of the
 * mapped corners. Exact round-trip with `projectBbox` for `/Rotate ∈
 * {0,90,180,270}` (INV-PROJ-1).
 */
export function unprojectRect(rect, proj) {
    const k = proj.cssPxPerDevicePx;
    // Undo the device→CSS ratio to return to the transform's output space.
    const dx = rect.x / k;
    const dy = rect.y / k;
    const dw = rect.w / k;
    const dh = rect.h / k;
    const [a, b, c, d, e, f] = proj.userSpaceToDevice;
    const det = a * d - b * c;
    if (det === 0) {
        // A non-invertible frame (scale 0) cannot be un-projected; return the
        // input coords verbatim rather than emit NaN. Fail-soft, not fail-quiet:
        // a zero-determinant projection means the page was never really painted.
        return [rect.x, rect.y, rect.w, rect.h];
    }
    const invert = (X, Y) => [
        (d * (X - e) - c * (Y - f)) / det,
        (-b * (X - e) + a * (Y - f)) / det,
    ];
    const corners = [
        invert(dx, dy),
        invert(dx + dw, dy),
        invert(dx, dy + dh),
        invert(dx + dw, dy + dh),
    ];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [ux, uy] of corners) {
        if (ux < minX)
            minX = ux;
        if (ux > maxX)
            maxX = ux;
        if (uy < minY)
            minY = uy;
        if (uy > maxY)
            maxY = uy;
    }
    return [minX, minY, maxX - minX, maxY - minY];
}
/**
 * Build a {@link PageProjection} from a pdf.js viewport `transform` 6-tuple.
 * `cssPxPerDevicePx` defaults to `1` (see the field docstring) — the transform
 * from `page.getViewport({scale})` already lands in the overlay's CSS-pixel
 * space. Callers on both the pdf.js and raster paths use this one constructor
 * (Ph1b G-3), so there is a single transform-construction path for both flag
 * states.
 */
export function projectionFromTransform(transform, cssPxPerDevicePx = 1) {
    return {
        userSpaceToDevice: [
            transform[0], transform[1], transform[2],
            transform[3], transform[4], transform[5],
        ],
        cssPxPerDevicePx,
    };
}
